<template>
  <div>
    <b-modal
    :id="modalId"
    v-model="modalVisible"
    modal-class="modal-fullscreen modal-tambah-kunjungan"
    scrollable
    centered
    :static="true"
    :title="modalTitle"
    cancel-variant="outline-secondary"
    @close="closeModal"
    @hidden="onModalHidden"
  >
    <validation-observer ref="PendaftaranKunjungan">
      <b-form ref="formPendaftaranKunjungan" class="mt-1">
        <!-- Compact Registration Header -->
        <div class="compact-registration-header">
          <b-row class="mb-2">
            <b-col sm="12" md="3">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Tanggal Pendaftaran"
                >
                  <label class="compact-label">Waktu Pendaftaran <span class="text-danger">*</span></label>
                  <input-date-time v-model="dateTimeDaftar" :disabled="disabled" size="sm" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="9">
              <b-form-group class="mb-2">
                <div class="d-flex align-items-center mb-25">
                  <label class="compact-label mb-0">Pasien <span class="text-danger">*</span></label>
                  <b-badge v-if="isMassMode && massPatients.length > 0" variant="primary" pill class="ml-50 font-small-3">
                    {{ massPatients.length }} pasien dipilih
                  </b-badge>
                </div>
                <vue-autosuggest
                  v-if="!pasien"
                  v-model="selectedPasienText"
                  :suggestions="filteredPasiens"
                  :limit="10"
                  :input-props="{
                    id: 'autosuggest__input',
                    class: 'form-control form-control-sm',
                    placeholder: isMassMode ? 'Cari dan tambahkan pasien...' : 'Cari: No.RM/KTP/BPJS/Nama',
                    disabled: data !== null || disabled
                  }"
                  :get-suggestion-value="pasienSuggestionValue"
                  @selected="pasienSelected"
                  @keyup="onSearchPasien"
                  @keydown="onSearchPasien"
                >
                  <template slot-scope="{ suggestion }">
                    <span v-if="search != '' && suggestion.item.formattedNama.toLowerCase().includes(search.toLowerCase())" class="my-suggestion-item">
                      {{ suggestion.item.formattedNama.substr(0, suggestion.item.formattedNama.toLowerCase().indexOf(search.toLowerCase())) }}<span v-if="suggestion.item.formattedNama.toLowerCase().includes(search.toLowerCase())" style="background-color: yellow;">{{ suggestion.item.formattedNama.substr(suggestion.item.formattedNama.toLowerCase().indexOf(search.toLowerCase()), search.length) }}</span>{{ suggestion.item.formattedNama.substr(suggestion.item.formattedNama.toLowerCase().indexOf(search.toLowerCase()) + search.length) }}
                    </span>
                    <span v-else class="my-suggestion-item">
                      {{ suggestion.item.formattedNama }}
                    </span>
                  </template>
                </vue-autosuggest>
                <b-form-input
                  v-else
                  :disabled="true"
                  v-model="selectedPasienText"
                  size="sm"
                />
                <small v-if="pasienError" class="text-danger">{{ pasienError }}</small>
                <!-- Mass Patient List -->
                <mass-patient-list
                  v-if="isMassMode"
                  :patients="massPatients"
                  :disabled="massSubmitting"
                  :show-nomor-polis="nomorKartuPenjaminRequired"
                  class="mt-50"
                  @remove="removePasienFromMassList"
                  @update-polis="updateMassPatientPolis"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <!-- Compact Destination and Insurance Section -->
        <div class="compact-destination-section">
          <!-- Destination Row -->
          <b-row class="mb-2">
            <b-col sm="6" md="4">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Tujuan Kunjungan"
                >
                  <label class="compact-label">Tujuan <span class="text-danger">*</span></label>
                  <v-select
                    v-model="tujuan.selectedInstalation"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="clearableVSelect"
                    label="name"
                    :options="instalations"
                    placeholder="Pilih Tujuan"
                    :state="errors.length > 0 ? false : null"
                    :disabled="disabled || isPatientMissing"
                    class="select-size-sm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <!-- Unit auto-selected from v2 parent-child cascade -->
            <input type="hidden" :value="tujuan.selectedUnit && tujuan.selectedUnit.id" />
            <b-col sm="6" md="4">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Tujuan Ruangan"
                >
                  <label class="compact-label">Ruangan <span class="text-danger">*</span></label>
                  <v-select
                    v-model="tujuan.selectedRuangan"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="clearableVSelect"
                    label="name"
                    :options="ruangans"
                    placeholder="Pilih Ruangan"
                    :disabled="disabled || isPatientMissing"
                    class="select-size-sm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="6" md="4">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Tujuan Dokter"
                >
                  <label class="compact-label">Dokter <span class="text-danger">*</span></label>
                  <v-select
                    v-model="tujuan.selectedDokter"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="clearableVSelect"
                    label="name"
                    :options="dokters"
                    placeholder="Pilih Dokter"
                    :disabled="disabled || isPatientMissing"
                    class="select-size-sm"
                  >
                    <template #option="option">
                      <div>
                        <div class="fw-bold">{{ option.name }}</div>
                        <div class="text-muted small">{{ option.schedule_info }}</div>
                      </div>
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Rujukan Row (only for direct ancillary visits with jenis_kunjungan >= 4) -->
          <b-row v-if="isDirectAncillaryVisit" class="rujukan-row">
            <b-col cols="12" class="mb-25">
              <div class="d-flex align-items-center">
                <feather-icon icon="FileTextIcon" size="14" class="mr-25 text-primary" />
                <span class="compact-label text-primary font-weight-bold">Rujukan Dokter Perujuk</span>
              </div>
            </b-col>
            <b-col sm="6" md="3">
              <b-form-group class="mb-2">
                <label class="compact-label">Klinik / Faskes Asal</label>
                <vue-autosuggest
                  v-model="rujukan.pkkSelected"
                  :suggestions="filteredPKKOptions"
                  :limit="10"
                  :input-props="{
                    class: 'form-control form-control-sm',
                    placeholder: 'Ketik nama klinik/faskes',
                    disabled: disabled || isPatientMissing
                  }"
                  @selected="pkkSelected"
                  @input="onSearchPkk"
                >
                  <template slot-scope="{ suggestion }">
                    <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                  </template>
                </vue-autosuggest>
              </b-form-group>
            </b-col>
            <b-col sm="6" md="3">
              <b-form-group class="mb-2">
                <label class="compact-label">Nama Dokter</label>
                <b-form-input
                  v-model="rujukan.dokter"
                  size="sm"
                  placeholder="Dr. Ahmad Yani"
                  :disabled="disabled || isPatientMissing"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6" md="3">
              <b-form-group class="mb-2">
                <label class="compact-label">Diagnosa</label>
                <vue-autosuggest
                  v-model="queryIcd"
                  :suggestions="filteredIcds"
                  :limit="50"
                  :input-props="{
                    class: 'form-control form-control-sm',
                    placeholder: 'Kode ICD',
                    disabled: disabled || isPatientMissing
                  }"
                  :get-suggestion-value="icdSuggestionValue"
                  @selected="icdSelected"
                  @input="onSearchIcd"
                >
                  <template slot-scope="{ suggestion }">
                    <span class="my-suggestion-item">{{ suggestion.item.code + ' - ' + suggestion.item.str }}</span>
                  </template>
                </vue-autosuggest>
              </b-form-group>
            </b-col>
            <b-col sm="6" md="3">
              <b-form-group class="mb-2">
                <label class="compact-label">No. Rujukan</label>
                <b-form-input
                  v-model="rujukan.nomorRujukan"
                  size="sm"
                  placeholder="No. Rujukan"
                  :disabled="disabled || isPatientMissing"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Insurance and Additional Options Row -->
          <b-row>
            <b-col sm="12" md="3">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Penjamin / Asuransi"
                >
                  <label class="compact-label">Penjamin <span class="text-danger">*</span></label>
                  <v-select
                    v-model="penjamin.selectedPenjamin"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="clearableVSelect"
                    label="title"
                    :options="effectivePenjaminOptions"
                    placeholder="Pilih Penjamin"
                    :disabled="disabled || isPatientMissing"
                    class="select-size-sm"
                  />
                  <small v-if="isMassMode" class="text-info font-small-3">BPJS tidak tersedia pada pendaftaran massal</small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col v-if="isNotUmum && !isMassMode" sm="12" :md="isBpjs ? 4 : 6">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  :rules="`${
                    (nomorKartuPenjaminRequired ? 'required' : '') +
                    (isBpjs ? '|min:13|max:13' : '')
                  }`"
                  name="Nomor Penjamin"
                >
                  <label class="compact-label">{{ isBpjs ? 'No. Kartu BPJS' : 'Nomor Polis' }} <span v-if="nomorKartuPenjaminRequired" class="text-danger">*</span></label>
                  <b-input-group>
                    <b-form-input
                      v-model="penjamin.nomorKartuPenjamin"
                      :placeholder="isBpjs ? 'No. Kartu BPJS' : 'Nomor Polis'"
                      :disabled="disabled || isPatientMissing"
                      :maxlength="isBpjs ? 13 : 25"
                      type="text"
                      size="md"
                    />
                    <b-input-group-append v-if="isBpjs">
                      <b-button variant="outline-success" size="sm" :disabled="isLoadingBpjsNoka" @click="openModalBpjsNoka">
                        <b-spinner v-if="isLoadingBpjsNoka" small />
                        <icon v-else icon="mdi:user" width="1rem" height="1rem" />
                      </b-button>
                      <b-button v-if="selectedPasien && selectedPasien.nik" variant="outline-success" size="sm" :disabled="isLoadingBpjsNik" @click="openModalBpjsNik">
                        <b-spinner v-if="isLoadingBpjsNik" small />
                        <icon v-else icon="mdi:user-card-details" width="1rem" height="1rem" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col v-if="isBpjs" sm="6" md="2">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Kelas Penjamin"
                  :disabled="disabled"
                >
                  <label class="compact-label">Kelas <span class="text-danger">*</span></label>
                  <v-select
                    v-model="penjamin.selectedKelasPenjamin"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="clearableVSelect"
                    label="title"
                    :options="$store.getters['masterdata/penjaminKelas']"
                    placeholder="Kelas"
                    :disabled="disabled || isPatientMissing"
                    class="select-size-sm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col v-if="hasPenjaminPerusahaan" sm="12" md="3">
              <b-form-group class="mb-2">
                <label class="compact-label">Perusahaan</label>
                <v-select
                  v-model="penjamin.selectedPenjaminPerusahaan"
                  :options="filteredPenjaminPerusahaan"
                  label="nama"
                  placeholder="Pilih perusahaan..."
                  :disabled="disabled || isPatientMissing"
                  :clearable="true"
                  class="select-size-sm"
                >
                  <template #option="{ nama, kode }">
                    <span>{{ nama }}</span>
                    <small v-if="kode" class="text-muted ml-50">({{ kode }})</small>
                  </template>
                  <template #selected-option="{ nama, kode }">
                    <span>{{ nama }}</span>
                    <small v-if="kode" class="text-muted ml-25">({{ kode }})</small>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col sm="12" :md="isBpjs ? 3 : (hasPenjaminPerusahaan ? 3 : 3)">
              <b-form-group class="mb-2">
                <label class="compact-label">Catatan</label>
                <b-input-group>
                  <b-form-input
                    v-model="penjamin.keterangan"
                    placeholder="Catatan/Keterangan"
                    :disabled="disabled || isPatientMissing"
                    size="md"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Additional Options Row (when applicable) -->
          <b-row v-if="isRanap || pakets.length" class="mb-2">
            <b-col v-if="isRanap" sm="12" md="6">
              <b-form-group class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  name="Kode Reservasi"
                >
                  <label class="compact-label">Reservasi Bed</label>
                  <b-input-group size="sm">
                    <b-form-input
                      v-if="tujuan.kodeReservasi"
                      v-model="tujuan.kodeReservasi.nama_bed"
                      placeholder="Kode Reservasi"
                      :disabled="disabled"
                      readonly
                    />
                    <b-form-input
                      v-else
                      placeholder="Kode Reservasi"
                      :disabled="disabled"
                      readonly
                    />
                    <b-input-group-append>
                      <b-button
                        v-if="!disabled"
                        variant="outline-primary"
                        size="sm"
                        v-b-modal.reservasi-bedpanel-modal
                      >
                        Reservasi
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col v-if="pakets.length && !isBpjs" sm="12" md="6">
              <b-form-group class="mb-2">
                <label class="compact-label">Paket (Opsional)</label>
                <v-select
                  v-model="tujuan.selectedPaket"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :clearable="true"
                  label="name"
                  :options="pakets"
                  placeholder="Pilih Paket"
                  :disabled="disabled || isPatientMissing"
                  class="select-size-sm"
                >
                  <template #option="{ name, tarif }">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>{{ name }}</span>
                      <b-badge v-if="tarif" variant="light-success" pill class="ml-50">
                        Rp {{ Number(tarif).toLocaleString('id-ID') }}
                      </b-badge>
                    </div>
                  </template>
                  <template #selected-option="{ name, tarif }">
                    <span>{{ name }}</span>
                    <b-badge v-if="tarif" variant="light-success" pill class="ml-50" style="font-size: 0.7rem">
                      Rp {{ Number(tarif).toLocaleString('id-ID') }}
                    </b-badge>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <b-row v-if="!isMassMode" class="mt-1">
          <PanelRekamMedis ref="panelRekamMedis" :visible="true" :is-bpjs="isBpjs" :is-rajal="isRajal" :disabled="disabled || isPatientMissing" :value="rekamMedis" :patient="selectedPasien" @input="rekamMedis = $event" />
        </b-row>
          <PanelKecelakaan ref="panelKecelakaan" :visible="!isMassMode" :value="kecelakaan" :disabled="disabled || isPatientMissing" @input="kecelakaan = $event" />
        <b-row v-if="!isMassMode" class="mt-1">
          <b-col sm="12" md="12" xl="12">
            <h5 @click="!disabled && (tanggungJawab.enabled = !tanggungJawab.enabled)" :class="{ 'cursor-pointer': !disabled }" class="mb-1">
              <i :class="tanggungJawab.enabled ? 'feather icon-chevron-down' : 'feather icon-chevron-right'" class="mr-1"></i>
              Penanggung Jawab
            </h5>
          </b-col>
          <b-col v-if="tanggungJawab.enabled" sm="12" md="12" xl="12">
            <b-card-text>
              <b-row>
                <b-col lg="6">
                  <b-form-group>
                    <v-select
                      v-model="tanggungJawab.selectedPasienRelation"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :clearable="clearableVSelect"
                      label="title"
                      :options="$store.getters['masterdata/shdk']"
                      placeholder="Hubungan dengan pasien"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="6">
                  <b-form-group>
                    <b-form-input
                      v-model="tanggungJawab.nama"
                      placeholder="Nama Keluarga tanpa Gelar"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="4">
                  <b-form-group>
                    <v-select
                      v-model="tanggungJawab.jkSelected"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :clearable="clearableVSelect"
                      label="title"
                      :options="$store.getters['masterdata/jk']"
                      placeholder="Jenis Kelamin"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="4">
                  <b-form-group>
                    <v-select
                      v-model="tanggungJawab.pendidikanSelected"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :clearable="clearableVSelect"
                      label="title"
                      :options="$store.getters['masterdata/pendidikan']"
                      placeholder="Pendidikan"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="4">
                  <b-form-group>
                    <v-select
                      v-model="tanggungJawab.pekerjaanSelected"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :clearable="clearableVSelect"
                      label="title"
                      :options="$store.getters['masterdata/pekerjaan']"
                      placeholder="Pekerjaan"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="12">
                  <b-form-group>
                    <b-form-input
                      v-model="tanggungJawab.alamat"
                      placeholder="Masukkan Alamat"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="6">
                  <b-form-group>
                    <b-form-input
                      v-model="tanggungJawab.telp"
                      placeholder="Ex. 08123456789"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
                <b-col lg="6">
                  <b-form-group>
                    <v-select
                      v-model="tanggungJawab.jenisKontakSelected"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :clearable="clearableVSelect"
                      label="title"
                      :options="$store.getters['masterdata/jenisKontak']"
                      placeholder="Jenis Kontak"
                      :disabled="disabled"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card-text>
          </b-col>
        </b-row>

        <!-- Mass Mode: Progress/Results Overlay -->
        <b-overlay v-if="isMassMode" :show="massSubmitting || massResults !== null" no-wrap>
          <template #overlay>
            <!-- Mass Submit Progress -->
            <div v-if="massSubmitting" class="text-center p-3" style="max-width: 400px;">
              <feather-icon icon="UsersIcon" size="36" class="text-primary mb-1" />
              <div class="font-weight-bold mb-50">
                Mendaftarkan pasien...
              </div>
              <div class="mb-1 text-muted font-small-3">
                {{ massPatients.filter(p => p.status === 'success').length + massPatients.filter(p => p.status === 'failed').length }} / {{ massPatients.length }}
              </div>
              <b-progress
                :max="massPatients.length"
                :value="massPatients.filter(p => p.status === 'success' || p.status === 'failed').length"
                variant="primary"
                height="12px"
                class="mb-1"
              />
              <div class="text-left" style="max-height: 200px; overflow-y: auto;">
                <div v-for="(entry, idx) in massPatients" :key="idx" class="d-flex align-items-center mb-25 font-small-3">
                  <span class="mr-50" style="width: 18px;">
                    <b-spinner v-if="entry.status === 'processing'" small type="grow" variant="warning" />
                    <feather-icon v-else-if="entry.status === 'success'" icon="CheckCircleIcon" size="14" class="text-success" />
                    <feather-icon v-else-if="entry.status === 'failed'" icon="XCircleIcon" size="14" class="text-danger" />
                    <feather-icon v-else icon="CircleIcon" size="14" class="text-secondary" />
                  </span>
                  <span>{{ entry.pasien.nama }}</span>
                </div>
              </div>
            </div>
            <!-- Mass Submit Results -->
            <div v-else-if="massResults" class="text-center p-3" style="max-width: 400px;">
              <feather-icon
                :icon="massResults.failedCount === 0 ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                size="42"
                :class="massResults.failedCount === 0 ? 'text-success' : 'text-warning'"
                class="mb-1"
              />
              <h5 class="mb-1">Pendaftaran Massal Selesai</h5>
              <div v-if="massResults.successCount > 0" class="text-success mb-25">
                <feather-icon icon="CheckIcon" size="14" class="mr-25" />
                {{ massResults.successCount }} pasien berhasil didaftarkan
              </div>
              <div v-if="massResults.failedCount > 0" class="text-danger mb-50">
                <feather-icon icon="XIcon" size="14" class="mr-25" />
                {{ massResults.failedCount }} pasien gagal
              </div>
              <div v-if="massResults.failed.length > 0" class="text-left mb-1" style="max-height: 150px; overflow-y: auto;">
                <div v-for="(f, idx) in massResults.failed" :key="idx" class="font-small-3 text-danger mb-25">
                  <strong>{{ f.pasien.nama }}:</strong> {{ f.error }}
                </div>
              </div>
              <b-button variant="primary" size="sm" @click="closeMassResults">
                Tutup
              </b-button>
            </div>
          </template>
        </b-overlay>

        <!-- Single Mode: SatuSehat Consent + Progress Overlay -->
        <b-overlay v-else :show="showDialogConfirmation" no-wrap @shown="onShown">
          <template #overlay>
            <div
              v-if="processingSubmit"
              class="text-center p-3 text-secondary rounded"
            >
              <feather-icon icon="ClockIcon" size="36" />
              <div class="mb-2">
                Mohon tunggu ...
              </div>
              <b-progress
                min="1"
                max="100"
                :value="counterProgress"
                variant="primary"
                height="12px"
                class="mx-n3"
              />
            </div>
            <div
              v-else
              ref="dialog"
              tabindex="-1"
              role="dialog"
              aria-modal="false"
              aria-labelledby="form-confirm-label"
              class="text-center p-3"
            >
              <b-card-text class="font-weight-bolder text-center" style="max-width: 300px;">
                <div class="mb-50">
                  <img
                    src="@/assets/images/svg/satusehat-horizontal.svg"
                    alt="SatuSehat"
                    height="64"
                  />
                </div>
                <div>
                  Saya menjamin bahwa pasien sudah membaca dan menyetujui
                  <a href="/documents/general-consent" target="_blank">Form Consent</a>
                  pembukaan data di
                    <span class="d-inline-flex align-items-center">
                      <img
                        src="@/assets/images/svg/satusehat.svg"
                        alt="SatuSehat"
                        height="14"
                        width="14"
                        class="mx-25"
                        style="vertical-align: middle;"
                      />
                      <strong>SATUSEHAT</strong>
                    </span>
                </div>
              </b-card-text>
              <div class="d-flex justify-content-center">
                <b-button
                  v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                  variant="outline-danger"
                  class="mr-3"
                  :disabled="processingSubmit"
                  @click="onConfirmSubmit(false)"
                >
                  Tidak
                </b-button>
                <b-button
                  v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                  variant="outline-success"
                  :disabled="processingSubmit"
                  @click="onConfirmSubmit(true)"
                >
                  YA
                </b-button>
              </div>
            </div>
          </template>
        </b-overlay>
      </b-form>
    </validation-observer>
    <b-sidebar
      v-if="pasienBpjsVisible"
      id="sidebar-pasien-bpjs"
      sidebar-class="sidebar-lg"
      :visible="pasienBpjsVisible"
      bg-variant="white"
      shadow
      backdrop
      left
      :title="`Cek Pasien By ${bpjsPasienResponse.method}`"
      @change="(val) => pasienBpjsVisible = val"
    >
      <b-card no-body>
        <b-card-body>
          <!-- Alert for inactive patient -->
          <b-alert
            v-if="!bpjsPasienResponse.aktif"
            show
            variant="danger"
            class="mb-1 p-1"
          >
            <strong>Warning:</strong> status BPJS tidak aktif.
          </b-alert>

          <b-list-group flush>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Nama</strong></span>
              <span>{{ bpjsPasienResponse.nama }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>No. Kartu</strong></span>
              <span>{{ bpjsPasienResponse.noKartu }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>NIK</strong></span>
              <span>{{ bpjsPasienResponse.nik }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Hubungan Keluarga</strong></span>
              <span>{{ bpjsPasienResponse.hubunganKeluarga }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Jenis Kelamin</strong></span>
              <span>{{ bpjsPasienResponse.sex }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Tanggal Lahir</strong></span>
              <span>{{ bpjsPasienResponse.tglLahir }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Tgl Mulai Aktif</strong></span>
              <span>{{ bpjsPasienResponse.tglMulaiAktif }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Tgl Akhir Berlaku</strong></span>
              <span>{{ bpjsPasienResponse.tglAkhirBerlaku }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Provider</strong></span>
              <span>{{ bpjsPasienResponse.provider }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Provider Gigi</strong></span>
              <span>{{ bpjsPasienResponse.providerGigi }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Kelas</strong></span>
              <span>{{ bpjsPasienResponse.kelas }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Jenis Peserta</strong></span>
              <span>{{ bpjsPasienResponse.jenisPeserta }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Golongan Darah</strong></span>
              <span>{{ bpjsPasienResponse.golDarah }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>No. HP</strong></span>
              <span>{{ bpjsPasienResponse.noHp }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Status Aktif</strong></span>
              <span>{{ bpjsPasienResponse.statusAktif }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between">
              <span><strong>Tunggakan</strong></span>
              <span>
                <template v-if="bpjsPasienResponse.tunggakan < 1">
                  {{ bpjsPasienResponse.tunggakan }}
                </template>
                <template v-else>
                  <strong class="text-danger">{{ formatMoney(bpjsPasienResponse.tunggakan) }}</strong>
                </template>
              </span>
            </b-list-group-item>
          </b-list-group>
        </b-card-body>
      </b-card>
    </b-sidebar>


    <template #modal-footer>
      <div class="d-flex justify-content-between w-100">
        <div class="d-flex align-items-center" style="gap: 0.5rem;">
          <b-button
            v-if="!isMassMode"
            variant="outline-primary"
            size="sm"
            @click="openEditPatientModal"
          >
            <feather-icon icon="Edit2Icon" class="mr-50" />
            Edit Detail Pasien
          </b-button>
          <b-button
            v-if="isMassModeAvailable && !disabled"
            :variant="isMassMode ? 'warning' : 'outline-warning'"
            size="sm"
            @click="toggleMassMode"
            :disabled="massSubmitting"
          >
            <feather-icon icon="UsersIcon" class="mr-50" />
            Registrasi Massal: {{ isMassMode ? 'ON' : 'OFF' }}
          </b-button>
        </div>

        <div class="d-flex" style="gap: 0.5rem;">
          <b-button
            variant="outline-secondary"
            @click="$bvModal.hide(modalId)"
          >
            <feather-icon icon="XIcon" class="mr-50" />
            Tutup
          </b-button>
          <b-button variant="warning" @click.prevent="resetForm" :disabled="massSubmitting">
            <feather-icon icon="RefreshCwIcon" class="mr-50" />
            Reset
          </b-button>
          <b-button variant="primary" @click.prevent="submitForm" :disabled="massSubmitting">
            <feather-icon :icon="isEditMode ? 'EditIcon' : 'CheckCircleIcon'" class="mr-50" />
            <template v-if="isMassMode">
              Daftarkan {{ massPatients.length }} Pasien
            </template>
            <template v-else>
              {{ isEditMode ? 'Update' : 'Daftarkan' }}
            </template>
          </b-button>
        </div>
      </div>
    </template>
    <b-modal id="reservasi-bedpanel-modal" v-if="this.tujuan && this.tujuan.selectedRuangan">
      <panel-bed
        :ruangan-id="`${this.tujuan.selectedRuangan.id}`"
        :ruangan-name="`${this.tujuan.selectedRuangan.name}`"
        @selected="selectBed"
      />
    </b-modal>

    <!-- Modal Update Pasien -->
    <ModalUpdate ref="modalUpdatePasien" />
    </b-modal>

    <!-- Sibling, not a child: it opens only after the visit modal has closed,
         and a child of a hidden b-modal would not render. -->
    <general-consent-modal
      :visible.sync="consentVisible"
      :pendaftaran-id="consentPendaftaranId"
      :pasien="consentPasien"
      context="post-registration"
    />
  </div>
</template>

<script>
import axiosIns from '@/libs/axios'
import Ripple from 'vue-ripple-directive'
import HasMoney from '@core/mixins/ui/money'
import PanelKecelakaan from './panels/PanelKecelakaan.vue'
import PanelRekamMedis from './panels/PanelRekamMedis.vue'
import PanelBed from './panels/PanelBed.vue'
import MassPatientList from './panels/MassPatientList.vue'
import ModalUpdate from '@/components/pasien/ModalPasienUpdate.vue'
import GeneralConsentModal from '@/components/pasien/GeneralConsentModal.vue'
import { debounce } from 'lodash';

export default {
  directives: {
    Ripple
  },
  components: {
    PanelKecelakaan,
    PanelRekamMedis,
    PanelBed,
    MassPatientList,
    ModalUpdate,
    GeneralConsentModal,
  },
  mixins: [HasMoney],
  props: {
    pasien: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean
    },
    data: {
      type: Object,
      default: null
    },
    modalId: {
      type: String,
      default: 'modal-tambah-kunjungan'
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    return {
      modalVisible: this.visible,
      rujukan: {
        pkkSelected: null,
        nomorRujukan: null,
        dateRujukan: this.$moment(now).format('YYYY-MM-DD'),
        dateRujukanFormatted: null,
        dokter: null,
        smf: null,
        diagnosa: null,
        selectedIcd: null
      },
      tujuan: {
        selectedInstalation: null,
        selectedUnit: null,
        selectedRuangan: null,
        // selectedSmf: null,
        selectedDokter: null,
        selectedPaket: null,
        kodeReservasi: null,
      },
      penjamin: {
        selectedPenjamin: null,
        nomorKartuPenjamin: null,
        selectedKelasPenjamin: null,
        nomorSEP: null,
        keterangan: null,
        selectedPenjaminPerusahaan: null
      },
      penjaminPerusahaanList: [],
      kecelakaan: {
        jenis_kecelakaan: null,
        no_laporan_polisi: null,
        penjamin: null,
        keterangan: null,
        tanggal_kejadian: null,
        suplesi: null,
        no_sep_suplesi: null,
        kode_provinsi: null,
        kode_kabupaten: null,
        kode_kecamatan: null,
        status: null,
      },
      rekamMedis: {
        kunjungan_sakit: true,
        keluhan: null,
        tinggi_badan: null,
        berat_badan: null,
        lingkar_perut: null,
        suhu_badan: null,
        imt: null,
        sistole: null,
        diastole: null,
        resp_rate: null,
        heart_rate: null,
      },
      tanggungJawab: {
        enabled: false,
        selectedPasienRelation: null,
        nama: null,
        jkSelected: null,
        pendidikanSelected: null,
        pekerjaanSelected: null,
        alamat: null,
        telp: null,
        jenisKontakSelected: null
      },
      nomorKartuPenjaminRequired: false,
      clearableVSelect: false,
      selectedTindakans: [],
      maxDateDaftar: today,
      dateDaftar: this.$moment(now).format('YYYY-MM-DD'),
      dateTimeDaftar: this.data ?
        this.$moment(this.data.tanggal_pendaftaran).format('YYYY-MM-DD HH:mm:ss') :
        this.$moment().format('YYYY-MM-DD HH:mm:ss'),
      dateDaftarFormatted: null,
      timeDaftar: this.$moment(now).format('HH:mm'),
      search: '',
      filteredPasiens: [],
      filteredIcds: [],
      instalations: [],
      units: [],
      ruangans: [],
      // smfs: [],
      dokters: [],
      pakets: [],
      allRuanganV2: [],
      selectedPasien: null,
      consentVisible: false,
      consentPendaftaranId: null,
      consentPasien: {},
      // Set only by a successful new single-mode registration, consumed by the
      // modal's `hidden` handler. Guards against opening the consent modal when
      // the visit modal is closed for any other reason.
      pendingConsentPendaftaranId: null,
      pasienError: null,
      pasiens: [],
      queryIcd: null,
      icd: [],
      provinces: [],
      cities: [],
      dristricts: [],
      tindakanPageLength: 5,
      searchTindakan: '',
      tindakans: [],
      tindakanTableHeader: [
        {
          label: 'checkbox',
          key: 'id',
          tdClass: 'w-10 text-align-center'
        },
        {
          label: 'tindakan',
          key: 'name'
        },
        {
          label: 'harga',
          key: 'formattedPrice'
        }
      ],
      pkkSuggest: {
        data: [
          {
            id: 1,
            name: 'RS. ABCD'
          },
          {
            id: 2,
            name: 'PUSKESMAS LKOPPASASA'
          }
        ]
      },
      filteredPKKOptions: [],
      showDialogConfirmation: false,
      processingSubmit: false,
      counterProgress: 1,
      interval: null,
      postedData: null,
      // Mass registration mode
      isMassMode: false,
      massPatients: [],
      massSubmitting: false,
      massResults: null,

      tempNoBpjs: '',
      pasienBpjsVisible: false,
      isLoadingBpjsNoka: false,
      isLoadingBpjsNik: false,
      bpjsPasienResponse: {
        method: 'NIK',
        noKartu: '',
        nik: '',
        nama: '',
        hubunganKeluarga: '',
        sex: '',
        tglLahir: '',
        tglMulaiAktif: '',
        tglAkhirBerlaku: '',
        provider: '',
        providerGigi: '',
        kelas: '',
        jenisPeserta: '',
        golDarah: '',
        noHp: '',
        statusAktif: '',
        tunggakan: 0,
        aktif: false,
      },
    }
  },
  computed: {
    modalTitle() {
      if (this.isMassMode) return 'Pendaftaran Kunjungan Massal'
      return this.isEditMode ? 'Edit Pendaftaran Kunjungan' : 'Tambah Pendaftaran Kunjungan';
    },
    isEditMode() {
      return !!this.data && !!this.data.id;
    },
    selectedPasienText: {
      get() {
        if (this.isMassMode) return this.search || ''
        if (this.selectedPasien?.nama) {
          return `${this.selectedPasien?.norm} - ${this.selectedPasien?.nama}`
        }
        return this.selectedPasien?.norm
      },
      set(text) {
        if (this.isMassMode) {
          this.search = text
          return
        }
        const norm = text.split(' - ')[0]
        const nama = text.split(' - ')[1]
        this.selectedPasien = { norm, nama }
      }
    },
    isRajal() {
      if (!this?.tujuan?.selectedRuangan) return false;
      return this.tujuan.selectedRuangan.jenis_kunjungan === 1;
    },
    age() {
      if (!this.selectedPasien) return -1;
      return this.$moment().diff(this.selectedPasien.tanggal_lahir, 'years')
    },
    isBpjs() {
      return this.penjamin?.selectedPenjamin?.value === 2
    },
    isNotUmum() {
      return this.penjamin?.selectedPenjamin?.value !== 1
    },
    filteredPenjaminPerusahaan() {
      if (!this.penjamin?.selectedPenjamin?.value || !this.penjaminPerusahaanList.length) return []
      // selectedPenjamin.value is the referensi id (mapped by ReferensiResources)
      return this.penjaminPerusahaanList.filter(p => p.referensi_id === this.penjamin.selectedPenjamin.value)
    },
    hasPenjaminPerusahaan() {
      return this.filteredPenjaminPerusahaan.length > 0
    },
    isRanap() {
      return this.tujuan?.selectedRuangan?.jenis_kunjungan === 3
    },
    // Mass mode is only available when creating new (not editing, not pre-filled patient)
    isMassModeAvailable() {
      return !this.data && !this.pasien
    },
    // In mass mode, filter out BPJS (value === 2) from penjamin options
    effectivePenjaminOptions() {
      const all = this.$store.getters['masterdata/penjamin']
      if (!this.isMassMode) return all
      return (all || []).filter(p => p.value !== 2)
    },
    // Check if patient is required - disable destination/penjamin fields when no patient selected in create mode
    isPatientMissing() {
      if (this.isMassMode) return this.massPatients.length === 0
      return !this.isEditMode && !this.selectedPasien
    },
    // Check if this is a direct ancillary visit (Lab/Radiology) requiring dokter perujuk
    isDirectAncillaryVisit() {
      const jenisKunjungan = this.tujuan && this.tujuan.selectedRuangan && this.tujuan.selectedRuangan.jenis_kunjungan
      return jenisKunjungan && jenisKunjungan >= 4
    },
  },
  watch: {
    data() {
      this.dateTimeDaftar = this.$moment(this.data.tanggal_pendaftaran).format('YYYY-MM-DD HH:mm:ss')
      this.tujuan.kodeReservasi = this.data.tujuan_pasien.kamar
    },
    penjamin: {
      handler(penjamin) {
        if (
          penjamin.selectedPenjamin &&
          penjamin.selectedPenjamin.title == 'Tanpa Asuransi / Umum'
        ) {
          this.nomorKartuPenjaminRequired = false
        } else {
          this.nomorKartuPenjaminRequired = true
        }
      },
      deep: true
    },
    visible(val) {
      this.modalVisible = val
      if (val) {
        this.init()
      }
    },
    'tujuan.selectedInstalation': {
      handler(data) {
        if (data && data.id) {
          this.fetchUnitInstalation()
        }
      },
      deep: true
    },
    'tujuan.selectedUnit': {
      handler(data) {
        if (data && data.id) {
          this.fetchRuangan()
        }
      },
      deep: true
    },
    'tujuan.selectedRuangan': {
      handler(data) {
        if (data && data.id) {
          // this.fetchSmf()
          this.fetchPaket()
          this.fetchDokter()
          if (
            this.penjamin?.selectedKelasPenjamin &&
            this.penjamin.selectedKelasPenjamin?.value
          ) {
            this.fetchTindakan()
          }
        }
      },
      deep: true
    },
    // 'tujuan.selectedSmf': {
    //   handler(data) {
    //     if (data && data.id) {
    //       this.fetchDokter()
    //     }
    //   },
    //   deep: true
    // },
    'kecelakaan.selectedProvinsi': {
      handler(data) {
        if (data && data.value) {
          this.fetchKabupaten()
        }
      },
      deep: true
    },
    'kecelakaan.selectedKabupaten': {
      handler(data) {
        if (data && data.value) {
          this.fetchKecamatan()
        }
      },
      deep: true
    },
    'tujuan.selectedKelasPenjamin': {
      handler(data) {
        if (data && data.value) {
          if (this.tujuan.selectedRuangan && this.tujuan.selectedRuangan.id) {
            this.fetchTindakan()
          }
        }
      },
      deep: true
    },
    'tujuan.kodeReservasi': {
      handler(data, old, v) {
      },
      deep: true
    },
    'penjamin.selectedPenjamin': {
      handler(newVal, oldVal) {
        this.setNoPenjamin()
        // Clear company selection when penjamin type changes
        if (newVal && oldVal && newVal.value !== oldVal.value) {
          this.penjamin.selectedPenjaminPerusahaan = null
        }
      },
      deep: true
    },
  },
  beforeMount() {
    const { norm, nama } = this.$route.query
    if (norm && nama) {
      this.selectedPasien = { norm, nama }
    }
  },
  mounted() {
    this._modalShowHandler = (bvEvent, modalId) => {
      if (modalId === this.modalId) {
        this.init()
      }
    }
    this.$root.$on('bv::modal::show', this._modalShowHandler)
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::show', this._modalShowHandler)
  },
  methods: {
    init() {
      var vm = this

      vm.provinces = vm.$store.getters['masterdata/provinsi']
      vm.penjaminDefault()
      vm.pasienError = null

      // Fetch ruangan v2 and penjamin perusahaan, then populate edit data
      Promise.all([
        vm.fetchRuanganV2(),
        vm.fetchPenjaminPerusahaan()
      ]).then(function () {
        if (vm.data) {
          vm.detailPendaftaran(vm.data)
        }
      })

      if (vm.pasien) {
        vm.selectedPasien = vm.pasien
        vm.filteredPasiens = [vm.pasien]
        vm.debounceFetchRm()
      }

      if (vm.$store.getters['masterdata/penjaminKelas'].length < 1) {
        vm.$store.dispatch('masterdata/getPenjaminKelas')
      }

      vm.ensurePenjamin()
    },
    fetchRuanganV2() {
      var vm = this
      var CACHE_TTL = 15 * 60 * 1000 // 15 minutes
      var now = Date.now()
      var lastFetch = vm._ruanganV2LastFetch || 0

      if (vm.allRuanganV2.length > 0 && (now - lastFetch) < CACHE_TTL) {
        vm.buildInstalationsFromV2()
        return Promise.resolve()
      }

      return axiosIns.get('/ruangan/v2/list', {
        params: { per_page: 200, status: 1 }
      }).then(function (res) {
        var data = (res.data && res.data.data) || []
        if (data.length > 0) {
          vm.allRuanganV2 = data
          vm._ruanganV2LastFetch = now
          vm.buildInstalationsFromV2()
        }
      }).catch(function (err) {
        console.error('Failed to fetch ruangan v2:', err)
      })
    },
    buildInstalationsFromV2() {
      // Find all parent items that have jenis 5 (Ruangan) children
      // These are the items for the first "Tujuan" dropdown
      var vm = this
      var leafParentIds = {}
      var i, item

      // Collect parent_ids of all jenis 5 (leaf ruangan) items
      for (i = 0; i < vm.allRuanganV2.length; i++) {
        item = vm.allRuanganV2[i]
        if (item.jenis === 5 && item.parent_id) {
          leafParentIds[item.parent_id] = true
        }
      }

      // Build instalations list from items that are parents of jenis 5
      var result = []
      for (i = 0; i < vm.allRuanganV2.length; i++) {
        item = vm.allRuanganV2[i]
        if (leafParentIds[item.id]) {
          result.push({
            id: item.id,
            name: item.deskripsi,
            jenis: item.jenis,
            jenis_kunjungan: item.jenis_kunjungan
          })
        }
      }

      vm.instalations = result
    },
    ensurePenjamin() {
      var vm = this
      var CACHE_TTL = 15 * 60 * 1000 // 15 minutes
      var now = Date.now()
      var cached = vm.$store.getters['masterdata/penjamin']
      var lastFetch = vm._penjaminLastFetch || 0

      if (cached && cached.length > 0 && (now - lastFetch) < CACHE_TTL) {
        return Promise.resolve()
      }

      return axiosIns.get('/referensi/10?active=true').then(function (res) {
        var result = res.data.data || []
        if (result.length > 0) {
          vm.$store.commit('masterdata/SET_PENJAMIN', result)
          vm._penjaminLastFetch = now
        }
      }).catch(function (err) {
        console.error('Failed to fetch penjamin:', err)
      })
    },
    fetchPenjaminPerusahaan() {
      var vm = this
      if (vm.penjaminPerusahaanList.length > 0) return Promise.resolve()
      return axiosIns.get('/penjamin-perusahaan').then(function (res) {
        vm.penjaminPerusahaanList = res.data.data || res.data || []
      }).catch(function (err) {
        console.error('Failed to fetch penjamin perusahaan:', err)
      })
    },
    openEditPatientModal() {
      if (this.selectedPasien) {
        this.$refs.modalUpdatePasien.openModal(this.selectedPasien)
      } else {
        this.$dekatoast.warning('Pilih pasien terlebih dahulu')
      }
    },
    getSelectedJadwalId() {
      if (!this.tujuan?.selectedDokter?.jadwal) {
        return null
      }
      
      // Find the active schedule for today
      const todaySchedule = this.tujuan.selectedDokter.jadwal.find(
        schedule => schedule.is_today && schedule.is_active
      )
      
      return todaySchedule ? todaySchedule.id : null
    },
    closeModal() {
      this.modalVisible = false
      this.$store.dispatch('app/removeOperation', ['createPasien'])
      if (this.$refs.panelRekamMedis) this.$refs.panelRekamMedis.unsetData()
      if (this.$refs.panelKecelakaan) this.$refs.panelKecelakaan.unsetData()
      this.isMassMode = false
      this.massPatients = []
      this.massResults = null
      this.massSubmitting = false
      this.$emit('closeModal', this.postedData)
    },
    penjaminDefault() {
      const penjamins = this.$store.getters['masterdata/penjamin']
      const penjaminKelas = this.$store.getters['masterdata/penjaminKelas']
      const nonKelasHak = penjaminKelas.filter((v) => v.title == 'Non Kelas')

      this.penjamin.selectedPenjamin = penjamins[0]

      if (nonKelasHak.length > 0) {
        this.penjamin.selectedKelasPenjamin = nonKelasHak[0]
      }
    },
    dateFormat(ctx) {
      this.dateDaftarFormatted = ctx.selectedFormatted
    },
    dateFormatRujukan(ctx) {
      this.rujukan.dateRujukanFormatted = ctx.selectedFormatted
    },
    dateFormatKecelakaan(ctx) {
      this.kecelakaan.dateKecelakaanFormatted = ctx.selectedFormatted
    },
    onSearchPkk(text) {
      if (text === '' || text === undefined) {
        return
      }

      const filteredData = this.pkkSuggest.data
        .filter(
          (item) => item.name.toLowerCase().indexOf(text.toLowerCase()) > -1
        )
        .slice(0, 10)
      this.filteredPKKOptions = [
        {
          data: filteredData
        }
      ]
    },
    pkkSelected(i) {
      this.rujukan.pkkSelected = i.item
    },
    rujukanBaru() {
      this.rujukan.nomorRujukan = null
      this.rujukan.dateRujukan = this.$moment().format('YYYY-MM-DD')
    },
    onSearchPasien: debounce(async function (event) {
      const query = event.target.value
      if (query === '' || query === undefined) {
        return
      }

      try {
        this.search = query
        const res = await axiosIns.get(`/pasien/search/${query}`)
        this.filteredPasiens = [{
          data: res.data.data.map((pasien) => ({...pasien, formattedNama: `${pasien.norm} - ${pasien.nama}`}))
        }]
      } catch (err) {
      }
    }, 300),
    pasienSelected(i) {
      if (this.isMassMode) {
        this.addPasienToMassList(i.item)
        return
      }
      this.selectedPasien = i.item
      this.pasienError = null
      this.setNoPenjamin()
      this.debounceFetchRm()
    },
    async fetchRm() {
      // Safety check: ensure we have a valid patient with NORM
      if (!this.selectedPasien || !this.selectedPasien.norm) {
        console.warn('fetchRm called but selectedPasien.norm is not available')
        return
      }

      try {
        const { data } = await axiosIns.get(`/pasien/${this.selectedPasien.norm}/pendaftaran/recent`)
        const { medical_record, pasien } = data.data
        if (!medical_record) return;

        // Load anthropometry data
        if (medical_record.berat_badan) this.rekamMedis.berat_badan = medical_record.berat_badan
        if (medical_record.tinggi_badan) this.rekamMedis.tinggi_badan = medical_record.tinggi_badan
        if (medical_record.lingkar_perut) this.rekamMedis.lingkar_perut = medical_record.lingkar_perut
        if (medical_record.suhu_badan) this.rekamMedis.suhu_badan = medical_record.suhu_badan
        if (medical_record.berat_badan && medical_record.tinggi_badan)
          this.rekamMedis.imt = (parseInt(medical_record.berat_badan) / (parseInt(medical_record.tinggi_badan) / 100) ** 2).toFixed(2)

        // Load vital signs data
        if (medical_record.sistole) this.rekamMedis.sistole = medical_record.sistole
        if (medical_record.diastole) this.rekamMedis.diastole = medical_record.diastole
        if (medical_record.heart_rate) this.rekamMedis.heart_rate = medical_record.heart_rate
        if (medical_record.respiratory_rate) this.rekamMedis.resp_rate = medical_record.respiratory_rate

        // Load other data
        if (pasien.no_bpjs) this.tempNoBpjs = pasien.no_bpjs
      } catch (error) {
        console.error('Error fetching medical record:', error)
      }
    },
    debounceFetchRm: debounce(function () {
      this.fetchRm()
    }, 300),
    pasienSuggestionValue(suggestion) {
      return `${suggestion.item.norm} - ${suggestion.item.nama}`
    },
    async onSearchIcd(event) {
      const query = this.queryIcd
      if (query === '' || query === undefined || query.length < 5) {
        return
      }

      try {
        const { data } = await axios.get(`icd?search=${query}`)

        if (data.data.length > 0) {
          this.filteredIcds = [
            {
              data: data.data.map((icd) => ({
                id: icd.id,
                code: icd.CODE,
                str: icd.STR
              }))
            }
          ]
        }
      } catch (err) {
        this.$dekatoast.error('Tidak dapat memuat data ICD')
      }
    },
    icdSelected(i) {
      this.rujukan.selectedIcd = i.item
    },
    icdSuggestionValue(suggestion) {
      return `${suggestion.item.code} - ${suggestion.item.str}`
    },
    fetchUnitInstalation() {
      if (!this.tujuan.selectedInstalation) {
        return
      }

      this.units = []
      this.tujuan.selectedUnit = null
      this.ruangans = []
      this.tujuan.selectedRuangan = null
      this.tujuan.pakets = []
      this.tujuan.selectedPaket = null
      this.dokters = []
      this.tujuan.selectedDokter = null
      this.tujuan.kodeReservasi = this.data?.tujuan_pasien?.kamar

      // With v2 parent-child, the selected "Tujuan" IS the direct parent of leaf ruangan.
      // Auto-set unit to the selected instalation itself (single entry) to keep cascade working.
      var selectedId = this.tujuan.selectedInstalation.id
      this.units = [{ id: selectedId, name: this.tujuan.selectedInstalation.name }]
      this.tujuan.selectedUnit = this.units[0]
    },
    fetchRuangan() {
      if (!this.tujuan.selectedUnit) {
        return
      }

      this.ruangans = []
      this.tujuan.selectedRuangan = null
      this.tujuan.pakets = []
      this.tujuan.selectedPaket = null
      this.dokters = []
      this.tujuan.selectedDokter = null
      this.tujuan.kodeReservasi = this.data?.tujuan_pasien?.kamar

      // Filter jenis 5 (leaf ruangan) children from v2 data by parent_id
      var parentId = String(this.tujuan.selectedUnit.id)
      var result = []
      for (var i = 0; i < this.allRuanganV2.length; i++) {
        var item = this.allRuanganV2[i]
        if (item.jenis === 5 && String(item.parent_id) === parentId) {
          result.push({
            id: item.id,
            name: item.deskripsi,
            jenis_kunjungan: item.jenis_kunjungan
          })
        }
      }

      // Deduplicate by id
      var seen = {}
      this.ruangans = result.filter(function (r) {
        if (seen[r.id]) return false
        seen[r.id] = true
        return true
      })

      if (this.ruangans.length === 1) {
        this.tujuan.selectedRuangan = this.ruangans[0]
      }

      if (this.data && this.data.tujuan_pasien) {
        this.tujuan.selectedRuangan =
          this.ruangans.find(function (v) {
            return v.id == this.data.tujuan_pasien.ruangan.id
          }.bind(this)) || null
      }
    },
    // async fetchSmf() {
    //   if (!this.tujuan.selectedRuangan) {
    //     return
    //   }

    //   this.smfs = []
    //   this.tujuan.selectedSmf = null
    //   this.dokters = []
    //   this.tujuan.selectedDokter = null

    //   try {
    //     const { data } = await axios.get(
    //       `ruangan/smf/${this.tujuan.selectedRuangan.id}`
    //     )

    //     if (data.success === true && data.data.length > 0) {
    //       // this.smfs = this.arrayToLists(data.data, [
    //       //   "master_ruangan",
    //       //   "deskripsi",
    //       // ]);
    //       this.smfs = data.data.map((v) => {
    //         if (v.referensi_smf)
    //           return {
    //             id: v.referensi_smf.value,
    //             name: v.referensi_smf.title
    //           }
    //       })

    //       if (data.data.length == 1) {
    //         this.tujuan.selectedSmf = this.smfs[0]
    //         // this.fetchDokter();
    //       }

    //       if (this.data) {
    //         this.tujuan.selectedSmf =
    //           this.smfs.find((v) => (v.id = this.data.tujuan_pasien.smf_id)) ||
    //           null
    //       }
    //     }
    //   } catch (err) {
    //     this.$dekatoast.error('Tidak dapat memuat data Spesialis')
    //   }
    // },
    async fetchDokter() {
      if (!this.tujuan.selectedRuangan) {
        return
      }

      this.dokters = []
      this.tujuan.selectedDokter = null
      this.tujuan.kodeReservasi = this.data?.tujuan_pasien?.kamar

      try {
        // Format date for API parameter (YYYY-MM-DD)
        const selectedDate = this.$moment(this.dateTimeDaftar).format('YYYY-MM-DD')
        
        // First try with date parameter to get schedule information
        let { data } = await axios.get(
          `ruangan/dokter/${this.tujuan.selectedRuangan.id}/jadwal?date=${selectedDate}`
        )

        if (data.success === true && data.data.length > 0) {
          this.dokters = data.data.map((doctorData) => {
            if (!doctorData.master_dokter || doctorData.status == 0) return
            
            // Find today's schedule from jadwal array
            const todaySchedule = doctorData.jadwal?.find(schedule => schedule.is_today && schedule.is_active)
            const scheduleInfo = todaySchedule ? 
              `Praktek: ${todaySchedule.jam_praktek} - Quota: ${todaySchedule.quota}` : 
              'Tidak ada jadwal hari ini'
            
            return {
              id: doctorData.master_dokter.id,
              name: doctorData.master_dokter.master_pegawai.nama_dokter,
              master_dokter_id: doctorData.master_dokter.id,
              schedule_info: scheduleInfo,
              jadwal: doctorData.jadwal || []
            }
          }).filter((v) => v)

          if (data.data.length == 1) {
            this.tujuan.selectedDokter = this.dokters[0]
          }

          if (this.data) {
            this.tujuan.selectedDokter =
              this.dokters.find(
                (v) => (v.master_dokter_id === this.data.tujuan_pasien.dokter_id)
              ) || null
          }
        }
      } catch (err) {
        
        // Fallback: try without date parameter
        try {
          const { data } = await axios.get(
            `ruangan/dokter/${this.tujuan.selectedRuangan.id}`
          )

          if (data.success === true && data.data.length > 0) {
            this.dokters = data.data.map((doctorData) => {
              if (!doctorData.master_dokter || doctorData.status == 0) return
              return {
                id: doctorData.master_dokter.id,
                name: doctorData.master_dokter.master_pegawai.nama_dokter,
                master_dokter_id: doctorData.master_dokter.id,
                schedule_info: 'Jadwal tidak tersedia',
                jadwal: []
              }
            }).filter((v) => v)

            if (data.data.length == 1) {
              this.tujuan.selectedDokter = this.dokters[0]
            }

            if (this.data) {
              this.tujuan.selectedDokter =
                this.dokters.find(
                  (v) => (v.master_dokter_id === this.data.tujuan_pasien.dokter_id)
                ) || null
            }
          }
        } catch (fallbackErr) {
          this.$dekatoast.error('Tidak dapat memuat data dokter')
        }
      }
    },
    async fetchKabupaten() {
      if (!this.kecelakaan.selectedProvinsi) {
        return
      }

      this.dristricts = []
      this.kecelakaan.selectedKecamatan = null
      this.kecelakaan.selectedKabupaten = null

      try {
        const { data } = await axios.get(
          `wilayah/kota/${this.kecelakaan.selectedProvinsi.value}?jenis=2`
        )

        if (data.success === true && data.data.length > 0) {
          this.cities = data.data

          if (this.data && this.data.kecelakaan.kode_kabupaten) {
            this.kecelakaan.selectedKabupaten =
              this.cities.find(
                (v) => (v.value === this.data.kecelakaan.kode_kabupaten)
              ) || null
          }
        }
      } catch (err) {
        this.$dekatoast.error('Tidak dapat memuat data kota/kabupaten')
      }
    },
    async fetchKecamatan() {
      if (!this.kecelakaan.selectedKabupaten) {
        return
      }

      try {
        const { data } = await axios.get(
          `wilayah/kecamatan/${this.kecelakaan.selectedKabupaten.value}?jenis=3`
        )

        if (data.success === true && data.data.length > 0) {
          this.dristricts = data.data

          if (this.data && this.data.kecelakaan.kode_kecamatan) {
            this.kecelakaan.selectedKecamatan =
              this.dristricts.find(
                (v) => (v.value === this.data.kecelakaan.kode_kecamatan)
              ) || null
          }
        }
      } catch (err) {
        this.$dekatoast.error('Tidak dapat memuat data kecamatan')
      }
    },
    async fetchPaket() {
      try {
        const { data } = await this.$axios.get(
          `package/by-ruangan/${this.tujuan.selectedRuangan.id}`
        )
        this.pakets = data.packages

        if (data.packages == 1) {
          this.tujuan.selectedPaket = this.pakets[0]
        }

        if (this.data) {
          this.tujuan.selectedPaket =
            this.pakets.find((v) => (v.id === this.data.paket_id)) ||
            null
        }
      } catch (err) {
        this.$dekatoast.error('Tidak dapat memuat data paket')
      }
    },
    async submitForm() {
      if (this.isMassMode) {
        if (this.massPatients.length === 0) {
          this.pasienError = 'Tambahkan minimal 1 pasien'
          return
        }
        // Validate per-patient nomor polis when penjamin requires it
        if (this.nomorKartuPenjaminRequired) {
          var missingPolis = this.massPatients.filter(function (e) {
            return e.status === 'pending' && !e.nomor_polis
          })
          if (missingPolis.length > 0) {
            this.$dekatoast.warning('Lengkapi nomor polis untuk semua pasien')
            return
          }
        }
        this.$refs.PendaftaranKunjungan.validate().then((success) => {
          if (success) {
            // Mass mode: skip SatuSehat consent dialog (not applicable), submit directly
            this.submitMassMode(false)
          }
        })
        return
      }

      if (!this.selectedPasien) {
        this.pasienError = 'Pasien belum dipilih'
        return
      }

      this.$refs.PendaftaranKunjungan.validate().then((success) => {
        if (success) {
          this.showDialogConfirmation = true
        }
      })
    },
    resetForm() {
      this.selectedPasien = null
      this.massPatients = []
      this.massResults = null
      this.massSubmitting = false
      Object.keys(this.tujuan).forEach((index) => {
        this.tujuan[index] = null
      })
      Object.keys(this.rujukan).forEach((index) => {
        this.rujukan[index] = null
      })
      Object.keys(this.penjamin).forEach((index) => {
        this.penjamin[index] = null
      })
      this.penjaminDefault()
      Object.keys(this.kecelakaan).forEach((index) => {
        this.kecelakaan[index] = null
      })
      Object.keys(this.tanggungJawab).forEach((index) => {
        this.tanggungJawab[index] = null
      })
    },
    onShown() {
      // Focus the dialog prompt (only exists in single mode)
      if (this.$refs.dialog) this.$refs.dialog.focus()
    },
    onCancelSubmit() {
      this.showDialogConfirmation = false
    },
    onModalHidden() {
      // Fires on every close; the flag is what makes this specific to a
      // just-completed registration.
      if (!this.pendingConsentPendaftaranId) return
      this.pendingConsentPendaftaranId = null
      this.consentVisible = true
    },
    onConfirmSubmit(daftarkanSatuSehat) {
      if (this.processingSubmit) return
      this.counterProgress = 1
      this.processingSubmit = true

      const tindakans = []
      if (this.selectedTindakans.length > 0) {
        this.selectedTindakans.forEach((item, index, arr) => {
          tindakans.push(index)
        })
      }
      // Simulate an async request
      const form = {
        norm: this.selectedPasien.norm,
        // tanggal_pendaftaran: this.dateDaftar + ' ' + this.timeDaftar + ':00',
        tanggal_pendaftaran: this.dateTimeDaftar,
        oleh: 1,
        dokter_perujuk_id: null,
        tujuan: {
          ruangan_id: this.tujuan.selectedRuangan.id,
          // smf_id: this.tujuan.selectedSmf.id,
          dokter_id: this.tujuan.selectedDokter.master_dokter_id,
          jadwal_dokter_id: this.getSelectedJadwalId(),
          paket_id: this.tujuan?.selectedPaket ? this.tujuan.selectedPaket.id : null,
          kamar_id: this.tujuan?.kodeReservasi ? this.tujuan.kodeReservasi.id : null,
        },
        rujukan: {
          ppk: this.rujukan.pkkSelected ? this.rujukan.pkkSelected.id : null,
          nomor: this.rujukan.nomorRujukan,
          tanggal: this.rujukan.dateRujukan,
          dokter: this.rujukan.dokter,
          bagian_dokter: this.rujukan.smf,
          diagnosa_masuk: this.rujukan.diagnosa
            ? this.rujukan.diagnosa.id
            : null,
          icd: this.rujukan.selectedIcd ? this.rujukan.selectedIcd.id : null
        },
        penjamin: {
          jenis: this.penjamin.selectedPenjamin.value,
          nomor_penjamin: this.penjamin.nomorKartuPenjamin,
          kelas: this.penjamin?.selectedKelasPenjamin?.value,
          no_sep: this.penjamin.nomorSEP,
          keterangan: this.penjamin.keterangan,
          penjamin_perusahaan_id: this.penjamin.selectedPenjaminPerusahaan
            ? this.penjamin.selectedPenjaminPerusahaan.id
            : null
        },
        kecelakaan: this.kecelakaan?.status ? this.kecelakaan : null,
        penanggung_jawab_pasien: {
          hubungan: this.tanggungJawab.selectedPasienRelation
            ? this.tanggungJawab.selectedPasienRelation.value
            : null,
          nama: this.tanggungJawab.nama,
          jenis_kelamin: this.tanggungJawab.jkSelected
            ? this.tanggungJawab.jkSelected.value
            : null,
          pendidikan: this.tanggungJawab.pendidikanSelected
            ? this.tanggungJawab.pendidikanSelected.value
            : null,
          pekerjaan: this.tanggungJawab.pekerjaanSelected
            ? this.tanggungJawab.pekerjaanSelected.value
            : null,
          alamat: this.tanggungJawab.alamat,
          jenis_kontak: this.tanggungJawab.jenisKontakSelected
            ? this.tanggungJawab.jenisKontakSelected.value
            : null,
          no_kontak: this.tanggungJawab.telp
        },
        medical_record: {
          kunjungan_sakit: this.rekamMedis.kunjungan_sakit,
          keluhan: this.rekamMedis.keluhan,
          tinggi_badan: this.rekamMedis.tinggi_badan,
          berat_badan: this.rekamMedis.berat_badan,
          lingkar_perut: this.rekamMedis.lingkar_perut,
          suhu_badan: this.rekamMedis.suhu_badan,
          imt: this.rekamMedis.imt,
          sistole: this.rekamMedis.sistole || this.normalRm().sistole,
          diastole: this.rekamMedis.diastole || this.normalRm().diastole,
          respiratory_rate: this.rekamMedis.resp_rate || this.normalRm().resp_rate,
          heart_rate: this.rekamMedis.heart_rate || this.normalRm().heart_rate,
          promotif: this.rekamMedis.promotif,
        },
        tindakan_ids: tindakans,
        satu_sehat: daftarkanSatuSehat,
      }

      const method = this.isEditMode ? 'put' : 'post';
      const url = this.isEditMode ? `pendaftaran/${this.data.id}` : 'pendaftaran';

      axiosIns[method](url, form, {
        onUploadProgress: function (progressEvent) {
          this.counterProgress = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          )
        }.bind(this)
      })
        .then((res) => {
          this.counterProgress = 1
          this.processingSubmit = false
          this.showDialogConfirmation = false

          if (res.data.data && Object.keys(res.data.data).length > 0) {
            // Only a brand-new single-mode registration prompts for consent.
            // Edits must not nag, and mass mode never reaches this method.
            if (!this.isEditMode && !this.isMassMode && res.data.data.id) {
              this.consentPendaftaranId = res.data.data.id
              // Captured before resetForm() clears it; the consent modal needs
              // the patient to resolve a WhatsApp number.
              this.consentPasien = this.selectedPasien || {}
              this.pendingConsentPendaftaranId = res.data.data.id
            }

            this.$bvModal.hide(this.modalId)
            this.postedData = res.data.data
            this.$emit(this.isEditMode ? 'updated' : 'finish', this.selectedPasien.norm, res.data.data)
            this.resetForm()
            this.$dekatoast.success(`Pasien sukses ${this.isEditMode ? 'diperbarui' : 'didaftarkan'} antrean kunjungan`)
          }
        })
        .catch((err) => {
          this.counterProgress = 1
          this.processingSubmit = false
          this.showDialogConfirmation = false

          if (
            err.response &&
            err.response.data &&
            Object.keys(err.response.data.message).length > 0
          ) {
            for (const errors of Object.values(err.response?.data?.errors || {})) {
              for (const error of errors) {
                this.$dekatoast.error(error)
              }
            }
            if (!err.response.data?.errors && err.response.data?.message) this.$dekatoast.error(err.response.data?.message)
            if (!err.response.data?.errors && !err.response.data?.message) this.$dekatoast.error('Harap lengkapi isian data kunjungan')
          } else {
            this.$dekatoast.error(`Gagal ${this.isEditMode ? 'memperbarui' : 'mendaftarkan'} kunjungan. Harap hubungi tim IT`)
          }
        })
    },
    // --- Mass Registration Mode Methods ---
    toggleMassMode() {
      this.isMassMode = !this.isMassMode
      this.massPatients = []
      this.massResults = null
      this.massSubmitting = false
      this.pasienError = null
      this.selectedPasien = null
      this.search = ''
      this.filteredPasiens = []

      if (this.isMassMode) {
        // Reset penjamin if BPJS is selected
        if (this.penjamin?.selectedPenjamin?.value === 2) {
          this.penjaminDefault()
        }
      }
    },
    addPasienToMassList(pasien) {
      // Check for duplicates
      if (this.massPatients.find(e => e.pasien.norm === pasien.norm)) {
        this.$dekatoast.warning(`Pasien ${pasien.nama} sudah ada di daftar`)
        this.search = ''
        this.filteredPasiens = []
        return
      }
      this.massPatients.push({
        pasien,
        status: 'pending',
        error: null,
        // Pre-fill nomor polis from patient data if available
        nomor_polis: pasien.no_bpjs || pasien.no_polis || '',
      })
      // Clear search for next patient
      this.search = ''
      this.filteredPasiens = []
      this.pasienError = null
      // Focus back on the autosuggest input
      this.$nextTick(() => {
        const input = document.getElementById('autosuggest__input')
        if (input) input.focus()
      })
    },
    removePasienFromMassList(index) {
      this.massPatients.splice(index, 1)
    },
    updateMassPatientPolis({ index, value }) {
      if (this.massPatients[index]) {
        this.$set(this.massPatients[index], 'nomor_polis', value)
      }
    },
    normalRmForAge(patientAge) {
      if (patientAge >= 0 && patientAge < 5) {
        return {
          sistole: this.rand(95, 110),
          diastole: this.rand(56, 70),
          resp_rate: this.rand(24, 40),
          heart_rate: this.rand(80, 120),
        }
      }
      if (patientAge >= 0 && patientAge < 13) {
        return {
          sistole: this.rand(97, 112),
          diastole: this.rand(57, 71),
          resp_rate: this.rand(16, 30),
          heart_rate: this.rand(75, 118),
        }
      }
      if (patientAge >= 0 && patientAge < 18) {
        return {
          sistole: this.rand(112, 128),
          diastole: this.rand(66, 80),
          resp_rate: this.rand(12, 20),
          heart_rate: this.rand(60, 100),
        }
      }
      return {
        sistole: this.rand(90, 120),
        diastole: this.rand(60, 80),
        resp_rate: this.rand(12, 20),
        heart_rate: this.rand(60, 100),
      }
    },
    buildMassPayload(norm, satuSehat, nomorPolis, pasien) {
      var patientAge = pasien && pasien.tanggal_lahir
        ? this.$moment().diff(pasien.tanggal_lahir, 'years')
        : -1
      var vitals = this.normalRmForAge(patientAge)

      return {
        norm,
        tanggal_pendaftaran: this.dateTimeDaftar,
        oleh: 1,
        dokter_perujuk_id: null,
        tujuan: {
          ruangan_id: this.tujuan.selectedRuangan.id,
          dokter_id: this.tujuan.selectedDokter.master_dokter_id,
          jadwal_dokter_id: this.getSelectedJadwalId(),
          paket_id: this.tujuan?.selectedPaket ? this.tujuan.selectedPaket.id : null,
          kamar_id: null,
        },
        rujukan: {
          ppk: null,
          nomor: null,
          tanggal: null,
          dokter: null,
          bagian_dokter: null,
          diagnosa_masuk: null,
          icd: null,
        },
        penjamin: {
          jenis: this.penjamin.selectedPenjamin.value,
          nomor_penjamin: nomorPolis || this.penjamin.nomorKartuPenjamin,
          kelas: this.penjamin?.selectedKelasPenjamin?.value,
          no_sep: null,
          keterangan: this.penjamin.keterangan,
          penjamin_perusahaan_id: this.penjamin.selectedPenjaminPerusahaan
            ? this.penjamin.selectedPenjaminPerusahaan.id
            : null,
        },
        kecelakaan: null,
        penanggung_jawab_pasien: {
          hubungan: null,
          nama: null,
          jenis_kelamin: null,
          pendidikan: null,
          pekerjaan: null,
          alamat: null,
          jenis_kontak: null,
          no_kontak: null,
        },
        medical_record: {
          kunjungan_sakit: false,
          keluhan: null,
          tinggi_badan: null,
          berat_badan: null,
          lingkar_perut: null,
          suhu_badan: null,
          imt: null,
          sistole: vitals.sistole,
          diastole: vitals.diastole,
          respiratory_rate: vitals.resp_rate,
          heart_rate: vitals.heart_rate,
          promotif: null,
        },
        tindakan_ids: [],
        satu_sehat: satuSehat,
      }
    },
    async submitMassMode(satuSehat) {
      this.massSubmitting = true
      this.massResults = null
      let successCount = 0
      let failedCount = 0
      const failed = []

      for (let i = 0; i < this.massPatients.length; i++) {
        const entry = this.massPatients[i]
        if (entry.status === 'success') {
          successCount++
          continue
        }

        entry.status = 'processing'
        entry.error = null

        try {
          const payload = this.buildMassPayload(entry.pasien.norm, satuSehat, entry.nomor_polis, entry.pasien)
          await axiosIns.post('pendaftaran', payload)
          entry.status = 'success'
          successCount++
        } catch (err) {
          entry.status = 'failed'
          entry.error = this.extractMassError(err)
          failedCount++
          failed.push({ pasien: entry.pasien, error: entry.error })
        }
      }

      this.massSubmitting = false
      this.massResults = { successCount, failedCount, failed }

      if (failedCount === 0) {
        this.$dekatoast.success(`${successCount} pasien berhasil didaftarkan`)
        this.$emit('finish')
      } else {
        this.$dekatoast.warning(`${successCount} berhasil, ${failedCount} gagal`)
        this.$emit('finish')
      }
    },
    extractMassError(err) {
      const errors = err.response?.data?.errors
      if (errors) return Object.values(errors).flat().join('; ')
      return err.response?.data?.message || 'Gagal mendaftarkan'
    },
    closeMassResults() {
      this.massResults = null
      this.$bvModal.hide(this.modalId)
      this.resetForm()
    },
    // --- End Mass Registration Mode Methods ---

    normalRm() {
      if (this.age < 5) {
        return {
          sistole: this.rand(95, 110),
          diastole: this.rand(56, 70),
          resp_rate: this.rand(24, 40),
          heart_rate: this.rand(80, 120),
        }
      }
      if (this.age < 13) {
        return {
          sistole: this.rand(97, 112),
          diastole: this.rand(57, 71),
          resp_rate: this.rand(16, 30),
          heart_rate: this.rand(75, 118),
        }
      }
      if (this.age < 18) {
        return {
          sistole: this.rand(112, 128),
          diastole: this.rand(66, 80),
          resp_rate: this.rand(12, 20),
          heart_rate: this.rand(60, 100),
        }
      }
      return {
        sistole: this.rand(90, 120),
        diastole: this.rand(60, 80),
        resp_rate: this.rand(12, 20),
        heart_rate: this.rand(60, 100),
      }
    },
    rand(min, max) {
      return Math.floor(Math.random() * (max - min + 1) + min)
    },
    detailPendaftaran(data) {
      if (!data?.tujuan_pasien) return;

      // Find the ruangan in v2 data and resolve its parent for the Tujuan dropdown
      var ruanganId = data.tujuan_pasien.ruangan
        ? String(data.tujuan_pasien.ruangan.id)
        : null
      var ruanganItem = null
      if (ruanganId) {
        ruanganItem = this.allRuanganV2.find(function (r) {
          return String(r.id) === ruanganId
        })
      }

      if (ruanganItem && ruanganItem.parent_id) {
        // Set Tujuan to the parent of this ruangan
        this.tujuan.selectedInstalation =
          this.instalations.find(function (v) {
            return String(v.id) === String(ruanganItem.parent_id)
          }) || null
      } else if (data.tujuan_pasien.instalasi) {
        // Fallback: try old instalasi field
        this.tujuan.selectedInstalation =
          this.instalations.find(function (v) {
            return v.id == data.tujuan_pasien.instalasi.id
          }) || null
      }

      const datetime = data.tanggal_pendaftaran
      const dateRujukan = data.rujukan?.tanggal || new Date()

      this.dateDaftar = this.$moment(datetime).format('YYYY-MM-DD')
      this.timeDaftar = this.$moment(datetime).format('HH:mm')
      this.selectedPasien = data.pasien

      this.rujukan = {
        pkkSelected: data.rujukan?.ppk
          ? data.rujukan.ppk.deskripsi || null
          : null,
        nomorRujukan: data.rujukan?.nomor,
        dateRujukan: this.rujukan?.pkkSelected
          ? this.$moment(data.rujukan?.tanggal).format('YYYY-MM-DD')
          : null,
        dateRujukanFormatted: null,
        dokter: data.rujukan?.dokter,
        smf: data.rujukan?.smf,
        diagnosa: data.rujukan?.diagnosa,
        selectedIcd: data.rujukan?.icd
          ? this.icd.find((v) => v.id == data.rujukan.id) || null
          : null
      }

      this.penjamin = {
        selectedPenjamin:
          this.$store.getters['masterdata/penjamin'].find(
            (v) => v.value == data.penjamin.jenis
          ) || null,
        nomorKartuPenjamin: data.penjamin.nomor_penjamin,
        selectedKelasPenjamin:
          this.$store.getters['masterdata/penjaminKelas'].find(
            (v) => v.value == data.penjamin.kelas
          ) || null,
        nomorSEP: data.penjamin.no_sep,
        keterangan: data.penjamin.keterangan,
        selectedPenjaminPerusahaan: data.penjamin.penjamin_perusahaan_id
          ? this.penjaminPerusahaanList.find(
              (p) => p.id === data.penjamin.penjamin_perusahaan_id
            ) || null
          : null
      }

      if (data.penanggung_jawab_pasien) {
        this.tanggungJawab.enabled = true
      }

      this.tanggungJawab = {
        selectedPasienRelation:
          this.$store.getters['masterdata/shdk'].find(
            (v) => v.value == data.penanggung_jawab_pasien?.hubungan
          ) || null,
        nama: data.penanggung_jawab_pasien?.nama,
        jkSelected:
          this.$store.getters['masterdata/jk'].find(
            (v) => v.value == data.penanggung_jawab_pasien?.jenis_kelamin
          ) || null,
        pendidikanSelected:
          this.$store.getters['masterdata/pendidikan'].find(
            (v) => v.value == data.penanggung_jawab_pasien?.pendidikan
          ) || null,
        pekerjaanSelected:
          this.$store.getters['masterdata/pekerjaan'].find(
            (v) => v.value == data.penanggung_jawab_pasien?.pekerjaan
          ) || null,
        alamat: data.penanggung_jawab_pasien?.alamat,
        telp: data.penanggung_jawab_pasien?.no_kontak,
        jenisKontakSelected:
          this.$store.getters['masterdata/jenisKontak'].find(
            (v) => v.value == data.penanggung_jawab_pasien?.jenis_kontak
          ) || null
      }

      if (data.kecelakaan) this.kecelakaan = {
        jenis_kecelakaan: data.kecelakaan.jenis_kecelakaan,
        no_laporan_polisi: data.kecelakaan.no_laporan_polisi,
        penjamin: data.kecelakaan.penjamin,
        tanggal_kejadian: data.kecelakaan.tanggal_kejadian
          ? this.$moment(data.kecelakaan.tanggal_kejadian).format('YYYY-MM-DD')
          : null,
        suplesi: !!data.kecelakaan.suplesi,
        no_sep_suplesi: data.kecelakaan.no_sep_suplesi,
        kode_provinsi: data.kecelakaan.kode_provinsi,
        kode_kabupaten: data.kecelakaan.kode_kabupaten,
        kode_kecamatan: data.kecelakaan.kode_kecamatan,
        status: !!data.kecelakaan.status,
        keterangan: data.kecelakaan.keterangan,
      }

      this.rekamMedis.keluhan = data.medical_record.keluhan
      this.rekamMedis.tinggi_badan = data.medical_record.tinggi_badan
      this.rekamMedis.berat_badan = data.medical_record.berat_badan
      this.rekamMedis.lingkar_perut = data.medical_record.lingkar_perut
      this.rekamMedis.suhu_badan = data.medical_record.suhu_badan
      this.rekamMedis.imt = data.medical_record.imt
      this.rekamMedis.sistole = data.medical_record.sistole
      this.rekamMedis.diastole = data.medical_record.diastole
      this.rekamMedis.resp_rate = data.medical_record.respiratory_rate
      this.rekamMedis.heart_rate = data.medical_record.heart_rate
    },
    async fetchTindakan() {
      try {
        const { data } = await axios.get(
          `ruangan/tindakan/${this.tujuan.selectedRuangan.id}/${this.penjamin?.selectedKelasPenjamin?.value}`
        )

        if (data.success === true && data.data.length > 0) {
          const tindakans = data.data
            .filter((v) => v?.master_tindakan?.master_tarif_tindakan?.length)
            .map((v) => {
              if (
                v.master_tindakan &&
                v.master_tindakan.master_tarif_tindakan.length > 0
              ) {
                return {
                  id: v.id,
                  name: v.nama_tindakan,
                  price: v.master_tindakan.master_tarif_tindakan[0].tarif,
                  formattedPrice: new Intl.NumberFormat('id-ID', {
                    maximumSignificantDigits: 3
                  }).format(v.master_tindakan.master_tarif_tindakan[0].tarif)
                }
              }
            })

          this.tindakans = tindakans
        }
      } catch (err) {
        this.$dekatoast.error('Tidak dapat memuat data tindakan ruangan')
      }
    },
    setNoPenjamin() {
      if (this.penjamin?.selectedPenjamin?.value !== 2) return
      if (!this.selectedPasien || !this.selectedPasien.no_bpjs) return
      this.penjamin.nomorKartuPenjamin = this.tempNoBpjs || this.selectedPasien.no_bpjs
    },
    async openModalBpjsNoka() {
      if (!this.penjamin?.nomorKartuPenjamin) return;
      this.isLoadingBpjsNoka = true;


      try {
        // Try the fallback endpoint first (includes BPJS API + local cache)
        const { data } = await this.$axios.get(`/pasien/bpjs/noka-fallback/${this.penjamin.nomorKartuPenjamin}`)
        if (data.status) {
          this.parseBpjsPasien(data.data)
          this.bpjsPasienResponse.method = 'NOKA'
          this.pasienBpjsVisible = true

          // Show message indicating data source
          const sourceMsg = data.source === 'cache' ? ' (dari database)' : ' (dari BPJS)'
          this.$dekatoast.success(data.message + sourceMsg)
        } else {
          this.$dekatoast.error(data.message || 'Data tidak ditemukan')
        }
      } catch (error) {
        
        // Fallback to original BPJS ref endpoint if the new endpoint fails
        try {
          const { data } = await this.$axios.get(`/pasien/ref_bpjs/noka/${this.penjamin.nomorKartuPenjamin}`)
          // Let DekaToast handle BPJS response automatically
          if (data.metadata) {
            this.$dekatoast.info(data);
          }
          this.parseBpjsPasien(data.body)
          this.bpjsPasienResponse.method = 'NOKA'
          this.pasienBpjsVisible = true
        } catch (fallbackError) {
          const errorMsg = fallbackError?.response?.data?.message || error?.response?.data?.message || 'Gagal mengambil data BPJS'
          this.$dekatoast.error(errorMsg);
        }
      } finally {
        this.isLoadingBpjsNoka = false;
      }
    },
    async openModalBpjsNik() {
      if (!this.selectedPasien?.nik) return;
      this.isLoadingBpjsNik = true;
      try {
        // Try the fallback endpoint first (includes BPJS API + local cache)
        const { data } = await this.$axios.get(`/pasien/bpjs/nik-fallback/${this.selectedPasien.nik}`)
        if (data.status) {
          this.parseBpjsPasien(data.data)
          this.bpjsPasienResponse.method = 'NIK'
          this.pasienBpjsVisible = true

          // Show message indicating data source
          const sourceMsg = data.source === 'cache' ? ' (dari database)' : ' (dari BPJS)'
          this.$dekatoast.success(data.message + sourceMsg)
        } else {
          this.$dekatoast.error(data.message || 'Data tidak ditemukan')
        }
      } catch (error) {
        
        // Fallback to original BPJS ref endpoint if the new endpoint fails
        try {
          const { data } = await this.$axios.get(`/pasien/ref_bpjs/nik/${this.selectedPasien.nik}`)
          // Let DekaToast handle BPJS response automatically
          if (data.metadata) {
            this.$dekatoast.info(data);
          }
          this.parseBpjsPasien(data.body)
          this.bpjsPasienResponse.method = 'NIK'
          this.pasienBpjsVisible = true;
        } catch (fallbackError) {
          const errorMsg = fallbackError?.response?.data?.message || error?.response?.data?.message || 'Gagal mengambil data BPJS'
          this.$dekatoast.error(errorMsg)
        }
      } finally {
        this.isLoadingBpjsNik = false;
      }
    },
    parseBpjsPasien(data) {
      this.bpjsPasienResponse.noKartu = data?.noKartu || ''
      this.bpjsPasienResponse.nik = data?.noKTP || ''
      this.bpjsPasienResponse.nama = data?.nama || ''
      this.bpjsPasienResponse.hubunganKeluarga = data?.hubunganKeluarga || ''
      this.bpjsPasienResponse.sex = data?.sex || ''
      this.bpjsPasienResponse.tglLahir = data?.tglLahir || ''
      this.bpjsPasienResponse.tglMulaiAktif = data?.tglMulaiAktif || ''
      this.bpjsPasienResponse.tglAkhirBerlaku = data?.tglAkhirBerlaku || ''
      this.bpjsPasienResponse.provider = data?.kdProviderPst?.nmProvider || ''
      this.bpjsPasienResponse.providerGigi = data?.kdProviderGigi?.nmProvider || ''
      this.bpjsPasienResponse.kelas = data?.jnsKelas?.nama || ''
      this.bpjsPasienResponse.jenisPeserta = data?.jnsPeserta?.nama || ''
      this.bpjsPasienResponse.golDarah = data?.golDarah || ''
      this.bpjsPasienResponse.noHp = data?.noHP || ''
      this.bpjsPasienResponse.statusAktif = data?.ketAktif || ''
      this.bpjsPasienResponse.tunggakan = data?.tunggakan || 0
      this.bpjsPasienResponse.aktif = data?.aktif

      const kelas = this.$store.getters['masterdata/penjaminKelas'].find(x => `${x.title}`.toLowerCase() === `${data?.jnsKelas?.nama}`.toLowerCase())
      this.penjamin.selectedKelasPenjamin = kelas
    },
    selectBed(bed) {
      if (bed.status != 1) return
      this.tujuan.kodeReservasi = bed
      this.$root.$emit('bv::hide::modal', 'reservasi-bedpanel-modal')
    }
  }
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.modal-tambah-kunjungan .tab-content {
  height: 100%;
}

// NOTE: the footer-visibility fix for tablet viewports now lives globally in
// src/assets/scss/style.scss, scoped to `.modal-fullscreen .modal-dialog-scrollable`
// so every scrollable fullscreen modal benefits, not just this one.

// Compact registration sections
.compact-registration-header,
.compact-destination-section {
  background: #ffffff;
  border-radius: 0.25rem;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
}

// Compact label styling (reuse from PanelRekamMedis)
.compact-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.75rem;
  margin-bottom: 0.25rem !important;
  display: block;
}

// Compact form groups
.compact-registration-header .form-group,
.compact-destination-section .form-group {
  margin-bottom: 0.5rem !important;
}

// Compact select styling
.compact-select {
  .vs__dropdown-toggle {
    min-height: 32px;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
  }

  .vs__selected-options {
    padding: 2px 8px;
  }

  .vs__selected {
    font-size: 0.875rem;
    margin: 2px 2px 2px 0;
    padding: 0 0.25rem;
  }

  .vs__search {
    font-size: 0.875rem;
    padding: 4px 6px;
  }

  .vs__dropdown-menu {
    font-size: 0.875rem;
  }
}

// Small input styling adjustments
.compact-registration-header .form-control,
.compact-destination-section .form-control {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  height: auto;

  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
  }
}

// Input group styling for compact sections
.compact-registration-header .input-group-sm,
.compact-destination-section .input-group-sm {
  .form-control {
    height: 32px;
    padding: 0.25rem 0.5rem;
    font-size: 1rem;
  }

  .input-group-text {
    background-color: #e9ecef;
    border: 1px solid #ced4da;
    color: #495057;
    font-weight: 600;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    height: 32px;
    display: flex;
    align-items: center;
  }

  .btn {
    font-size: 0.775rem;
    padding: 0.25rem 0.5rem;
    height: 32px;
  }
}

// Row spacing
.compact-registration-header .mb-2,
.compact-destination-section .mb-2 {
  margin-bottom: 0.5rem !important;
}

// Form validation styling
.compact-registration-header small.text-danger,
.compact-destination-section small.text-danger {
  font-size: 0.875rem;
  margin-top: 0.125rem;
  display: block;
}

// Responsive adjustments
@media (max-width: 768px) {
  .compact-registration-header,
  .compact-destination-section {
    padding: 0.5rem;
    margin-bottom: 0.375rem;
  }

  .compact-label {
    font-size: 0.7rem;
  }

  .compact-registration-header .form-control,
  .compact-destination-section .form-control {
    font-size: 0.8rem;
  }

  .compact-select {
    .vs__dropdown-toggle {
      min-height: 28px;
    }

    .vs__selected,
    .vs__search,
    .vs__dropdown-menu {
      font-size: 0.8rem;
    }
  }
}

@media (max-width: 576px) {
  .compact-registration-header,
  .compact-destination-section {
    padding: 0.375rem;
  }

  .compact-registration-header .input-group-sm,
  .compact-destination-section .input-group-sm {
    .form-control {
      height: 28px;
      font-size: 0.8rem;
    }

    .input-group-text {
      font-size: 0.65rem;
      height: 28px;
    }

    .btn {
      font-size: 0.7rem;
      height: 28px;
    }
  }

  // More compact spacing on mobile
  .compact-registration-header .mb-2,
  .compact-destination-section .mb-2 {
    margin-bottom: 0.375rem !important;
  }

  .compact-registration-header .form-group,
  .compact-destination-section .form-group {
    margin-bottom: 0.375rem !important;
  }
}

// Rujukan row styling
.rujukan-row {
  border-left: 3px solid #7367f0;
  margin-left: 0.25rem;
  padding-left: 0.75rem;
  margin-bottom: 0.25rem;
}
</style>
